/**
 * Bill Runs (PRD 2026-10-21, US-003): one row per bill per month it is assigned
 * to, plus one row per expected site-month that has no bill.
 *
 * Month assignment, by the page's toggle:
 *   - 'period': every selected month the bill's period touches (billInMonth);
 *   - 'end':    only the month containing bill_to (no adjustment for a bill
 *               ending on the 1st).
 *
 * Which site-months need a bill, and whether one covers them, is the By NMI
 * grid's rule (lib/missingBills.buildMissingGrid over expectedInMonth): this
 * builds that grid and reads its cells, it does not re-decide them. A grid cell
 * is covered by any active bill whose period touches the month, so in 'end'
 * mode a covered cell with no bill ending in the month is "Covered by" that
 * (later) bill rather than missing. Archived bills never cover (the grid skips
 * them) and are shown only when the fetch included them.
 *
 * Bill statuses are buildBoardRows' (lib/boardRows), so Bill Runs cannot
 * disagree with the board about a bill.
 */
import type { PipelineData } from '../services/bulkReportService';
import type { BvBill } from '../types/billTypes';
import { isArchived } from '../utils/archiveScope';
import { billInMonth } from './billMonths';
import { billVerdict, type BillVerdict } from './billVerdict';
import { buildBoardRows, type BoardRow } from './boardRows';
import {
  buildMissingGrid, gridBillOf, siteNmi, type ExpectedSite, type MissingCell, type MissingTagRow,
} from './missingBills';
import { reportFigures } from './reportStatus';
import { enrolledDuring, expectedInMonth } from './siteStatus';

export type MonthMode = 'period' | 'end';

/** Has bill · Missing · Covered · Not expected (US-006 Coverage condition). */
export type BillRunCoverage = 'has_bill' | 'missing' | 'covered' | 'not_expected';

export const COVERAGE_ORDER: BillRunCoverage[] = ['has_bill', 'missing', 'covered', 'not_expected'];

export const COVERAGE_LABELS: Record<BillRunCoverage, string> = {
  has_bill: 'Has bill',
  missing: 'Missing',
  covered: 'Covered',
  not_expected: 'Not expected',
};

/** The report's totals, on the report's GST basis (billVerdict.compared). */
export type BillRunFigures = {
  expected: number;
  actual: number;
  /** actual − expected: positive is an overcharge. */
  variance: number;
  /** The report runs ex-GST (reportFigures.gstExcluded). */
  gstExcluded: boolean;
};

export type BillRunBillRow = {
  kind: 'bill';
  /** `${billId}|${month}` — unique: a bill appears at most once per month. */
  key: string;
  /** `YYYY-MM`. */
  month: string;
  siteId: string;
  /** The bill's site among the expected sites, or null (not enrolled, or filtered out). */
  site: ExpectedSite | null;
  board: BoardRow;
  archived: boolean;
  coverage: 'has_bill';
  /** The site is expected to have a bill this month (expectedInMonth). Coverage. */
  expected: boolean;
  /** The site was enrolled during the bill's own period (enrolledDuring): the
   *  bill counts in Outcome, the match rate and the client totals. */
  inScope: boolean;
  /** Null until the report exists. */
  figures: BillRunFigures | null;
};

export type BillRunMissingRow = {
  kind: 'missing';
  /** `${siteId}|${month}`. */
  key: string;
  month: string;
  siteId: string;
  site: ExpectedSite;
  nmi: string;
  /** missing; covered (Period end mode, a later bill spans the month); not_expected (tagged, greyed). */
  coverage: Exclude<BillRunCoverage, 'has_bill'>;
  /** The bill whose period spans the month, when covered. */
  coveredBy: { billId: string; from: string | null; to: string | null } | null;
  /** The month's active missing-bill tag. */
  tag: MissingTagRow | null;
  /** The site's latest active bill end date, any month; null when it has never had a bill. */
  lastBill: string | null;
};

export type BillRunRow = BillRunBillRow | BillRunMissingRow;

export type BillRunRowsInput = Pick<PipelineData, 'bills' | 'emails' | 'queue' | 'analyses' | 'sendHistory'> & {
  sites: ExpectedSite[];
  tags: MissingTagRow[];
  /** Site id → latest active bill_to, over all time (billRunsService.loadLastBills). */
  lastBillTo: ReadonlyMap<string, string>;
};

/** The selected months the bill is assigned to under `mode`. */
export function assignedMonths(bill: Pick<BvBill, 'bill_from' | 'bill_to'>, mode: MonthMode, months: readonly string[]): string[] {
  if (!bill.bill_from || !bill.bill_to) return [];
  if (mode === 'end') {
    const end = bill.bill_to.slice(0, 7);
    return months.includes(end) ? [end] : [];
  }
  return months.filter((m) => billInMonth(bill, m));
}

function figuresOf(row: BoardRow, verdict: BillVerdict | null): BillRunFigures | null {
  if (row.pipeline.current !== 'done') return null;
  const figures = reportFigures(row.bill);
  if (!figures) return null;
  const expected = verdict?.compared.expected ?? figures.shadowResult.total;
  const actual = verdict?.compared.invoice ?? figures.actualResult.total;
  return {
    expected,
    actual,
    variance: verdict?.variance ?? Math.round((actual - expected) * 100) / 100,
    gstExcluded: figures.gstExcluded,
  };
}

function cellTag(cell: MissingCell): MissingTagRow | null {
  return cell.kind === 'no_bill' || cell.kind === 'missing_invoice' ? cell.tag : null;
}

/**
 * The rows for `months` (`YYYY-MM`, any order) under `mode`. `verdictFor` is
 * useBillVerdicts' lookup (with each retailer's charge map); omitted, verdicts
 * are worked out without the map, as buildBoardRows does.
 */
export function buildBillRunRows(
  input: BillRunRowsInput,
  mode: MonthMode,
  months: readonly string[],
  verdictFor: (bill: BvBill) => BillVerdict | null = billVerdict,
): BillRunRow[] {
  const sorted = [...new Set(months)].sort();
  const verdicts = new Map<string, BillVerdict | null>();
  const verdictOf = (bill: BvBill) => {
    if (!verdicts.has(bill.id)) verdicts.set(bill.id, verdictFor(bill));
    return verdicts.get(bill.id) ?? null;
  };

  // Active and archived bills apart: twinCounter counts active bills only.
  const active = input.bills.filter((b) => !isArchived(b));
  const archived = input.bills.filter((b) => isArchived(b));
  const boardRows = [
    ...buildBoardRows({ ...input, bills: active, lastRun: null }, verdictOf),
    ...(archived.length ? buildBoardRows({ ...input, bills: archived, lastRun: null }, verdictOf) : []),
  ];

  const siteById = new Map(input.sites.map((s) => [s.id, s]));
  const billById = new Map(input.bills.map((b) => [b.id, b]));
  const rows: BillRunRow[] = [];
  // Site-months with an assigned active bill: never a missing row.
  const assigned = new Set<string>();

  for (const board of boardRows) {
    const { bill } = board;
    const site = siteById.get(bill.site_id) ?? null;
    const isArch = isArchived(bill);
    const figures = figuresOf(board, verdictOf(bill));
    for (const month of assignedMonths(bill, mode, sorted)) {
      if (!isArch) assigned.add(`${bill.site_id}|${month}`);
      rows.push({
        kind: 'bill',
        key: `${bill.id}|${month}`,
        month,
        siteId: bill.site_id,
        site,
        board,
        archived: isArch,
        coverage: 'has_bill',
        expected: !!site && expectedInMonth(site.bv_extn, month),
        inScope: !!site && enrolledDuring(site.bv_extn, bill.bill_from, bill.bill_to),
        figures,
      });
    }
  }

  // The By NMI grid over every active bill: it decides expected and covered.
  const grid = buildMissingGrid({
    sites: input.sites,
    bills: boardRows.filter((r) => !isArchived(r.bill)).map(gridBillOf),
    tags: input.tags,
    months: sorted,
  });

  for (const gridRow of grid.rows) {
    gridRow.cells.forEach((cell, i) => {
      if (cell.kind === 'not_enrolled') return;
      const month = sorted[i];
      const siteId = gridRow.site.id;
      if (assigned.has(`${siteId}|${month}`)) return;
      const tag = cellTag(cell);
      const base = {
        kind: 'missing' as const, key: `${siteId}|${month}`, month, siteId, site: gridRow.site, nmi: siteNmi(gridRow.site), tag,
        lastBill: input.lastBillTo.get(siteId) ?? null,
      };
      if (cell.kind !== 'no_bill') {
        // A bill's period spans the month but none is assigned to it: only in
        // Period end mode, where the bill ends in a later month.
        const bill = billById.get(cell.billId);
        rows.push({
          ...base,
          coverage: 'covered',
          coveredBy: { billId: cell.billId, from: bill?.bill_from ?? null, to: bill?.bill_to ?? null },
        });
        return;
      }
      rows.push({ ...base, coverage: tag?.tag === 'not_expected' ? 'not_expected' : 'missing', coveredBy: null });
    });
  }

  return rows;
}
